import type { ReactNode } from 'react'
import type { SignupPath } from '@lb/domain'
import { Link } from '@tanstack/react-router'
import { useTranslation } from '@lb/i18n'
import { Button, IconCheck, IconChevronLeft, IconMoon, IconSun, Logo, LogoMark, cn, useTheme } from '@lb/ui'

import { CompactLanguageToggle, LanguageToggle } from '../../app/shell/language-toggle'

/**
 * The funnel's chrome (spec §3).
 *
 * A 5-step progress indicator, **hidden on landing, welcome and login** — those
 * are not steps, and a progress bar on a success screen implies there is more
 * to do.
 *
 * Labels differ per path, which is the point of the registry: a practitioner
 * has no crew step and an organisation buys seats rather than a subscription,
 * so a shared label list would be wrong for three of the four paths.
 */

/**
 * Translation keys, resolved at render time. The old registry hardcoded French
 * strings — legible only in fr, and rendered as raw French inside an English UI
 * (bug #3 in the round-25 landing review). Every label is `onboarding.step.<k>`
 * in both bundles.
 */
export const STEP_LABEL_KEYS: Record<SignupPath, string[]> = {
  PARENT: ['step.plan', 'step.aboutYou', 'step.yourCrew', 'step.confirm', 'step.pay'],
  PRO: ['step.plan', 'step.aboutYou', 'step.yourProfile', 'step.confirm', 'step.pay'],
  TSP: ['step.plan', 'step.aboutYou', 'step.yourProfile', 'step.confirm', 'step.pay'],
  ORG: ['step.plan', 'step.aboutYou', 'step.organisation', 'step.confirm', 'step.billing'],
}

export interface FunnelShellProps {
  /** 0-based. Omit to hide the indicator entirely — landing, welcome, login. */
  step?: number
  path?: SignupPath
  /** `.ob-eyebrow` — the small uppercase line above the title. Landing only. */
  eyebrow?: string
  /** Renders `.ob-back` above the title. Omit on screens with nowhere to go back to. */
  back?: { label: string; onClick: () => void }
  /** `.ob-main.narrow` (620px) / `.mid` (760px); default is the 1080px column. */
  width?: 'narrow' | 'mid'
  title: string
  lede?: string
  children: ReactNode
  footer?: ReactNode
  /** Wider, for the plan grid. */
  wide?: boolean
}

export function FunnelShell({
  step,
  path = 'PARENT',
  eyebrow,
  back,
  width,
  title,
  lede,
  children,
  footer,
  wide,
}: FunnelShellProps) {
  const { t } = useTranslation()
  const labels = STEP_LABEL_KEYS[path].map((k) => t(`onboarding.${k}`))
  const showProgress = step !== undefined

  return (
    /*
     * A flex column, so the footer sits at the bottom of a short page.
     *
     * `min-h-dvh` alone fills the viewport but leaves header/main/footer in
     * normal flow — on the landing page that stacked to 109px less than the
     * screen, so the footer floated mid-page with canvas continuing beneath it.
     * `flex-1` on main absorbs the slack.
     */
    <div className="flex min-h-dvh flex-col bg-canvas">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex h-[var(--topbar-h)] max-w-[1100px] items-center px-5">
          {/*
            * Phones get the star alone and `FR · EN` below. The full cluster —
            * lockup (185px), « Langue · Français English » (202px), theme and
            * « Se connecter » — measured 568px, so on a 390px phone the whole
            * signup page scrolled sideways (2 Oct 2026, from a member's phone).
            */}
          <span className="sm:hidden" aria-label={t('brand.name')} role="img">
            <LogoMark size={32} className="text-ink" />
          </span>
          <span className="hidden sm:inline-flex">
            <Logo size={32} />
          </span>
          {/*
            * The public pages had no language control at all.
            *
            * A visitor who lands on the French page and reads English had no
            * way out — and now that each language is a real URL, they may well
            * have arrived on the wrong one from a shared link. The switcher was
            * only ever in the authenticated shell, which is the half of the
            * product a visitor has not reached yet.
            */}
          {/*
            * The right-hand cluster the prototype's `.ob-topbar` carries and we
            * did not: the theme control, and a way in for someone who already
            * has an account.
            *
            * The log-in link is the one that mattered. This is a pricing page —
            * a returning member landing here had no signed-in path off it, and
            * the only route to /login was knowing the URL.
            */}
          <div className="ms-auto flex items-center gap-2 sm:gap-3">
            <div className="sm:hidden">
              <CompactLanguageToggle />
            </div>
            <div className="hidden sm:block">
              <LanguageToggle />
            </div>
            <ThemeToggle />
            <span className="hidden text-[13px] text-slate sm:inline">{t('onboarding.alreadyMember')}</span>
            <Link
              to="/login"
              className="inline-flex min-h-[40px] items-center rounded-full border border-border px-4 text-[13.5px] font-semibold text-ink hover:border-ink-soft"
            >
              {t('onboarding.signIn')}
            </Link>
          </div>
        </div>
      </header>

      <main
        id="main"
        /* Same one-declaration rule as the app shell: an inline padding-bottom
         * here silently killed `pb-20`. */
        className={cn(
          'mx-auto w-full flex-1 px-5 pt-8 pb-[calc(80px+var(--consent-h,0px))] md:pt-12',
          wide ? 'max-w-[1080px]' : width === 'mid' ? 'max-w-[760px]' : 'max-w-[620px]',
        )}
      >
        {back ? <BackLink onClick={back.onClick}>{back.label}</BackLink> : null}
        {showProgress ? (
          /*
           * Numbered-circle progress per the approved onboarding HTML
           * (`ob-shared.jsx` `ObProgress`). Each step is a circle joined by a
           * short rule; a done step is a checkmark on Vert (D4: completed),
           * current is dark filled with white number, upcoming is a light
           * outline with muted number.
           *
           * `justify-center` matches the approved centered layout; a wrap on
           * narrow viewports keeps every label visible instead of clipping.
           */
          <ol
            className="mb-10 flex flex-wrap items-center justify-center gap-x-3 gap-y-2"
            aria-label={t('onboarding.stepOf', { n: step + 1, total: labels.length })}
          >
            {labels.map((label, i) => {
              const done = i < step
              const current = i === step
              return (
                <li
                  key={label}
                  aria-current={current ? 'step' : undefined}
                  className="flex items-center gap-2 text-[13px]"
                >
                  <span
                    className={cn(
                      'inline-flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold',
                      done && 'bg-success text-ink-on-tint',
                      current && 'bg-ink text-white',
                      !done && !current && 'border border-border-deep bg-surface text-muted',
                    )}
                  >
                    {done ? (
                      <IconCheck size={14} title={null} />
                    ) : (
                      i + 1
                    )}
                  </span>
                  <span className={cn(current ? 'font-semibold text-ink' : 'text-muted')}>
                    {label}
                  </span>
                  {i < labels.length - 1 ? (
                    <span aria-hidden className="hidden w-6 border-t border-border-deep sm:inline-block" />
                  ) : null}
                </li>
              )
            })}
          </ol>
        ) : null}

        {/*
         * Centered, because `.ob-h1` is `text-align: center` on EVERY funnel
         * screen in the prototype, not just the landing — the funnel is a
         * narrow centered column throughout and we had it flush left.
         *
         * `text-wrap: balance` is the prototype's too: it is what stops a
         * two-line title breaking after one short word.
         */}
        {eyebrow ? (
          <p className="mb-2.5 text-center text-[12px] font-bold uppercase tracking-[0.14em] text-ink">
            {eyebrow}
          </p>
        ) : null}
        <h1 className="text-balance text-center font-serif text-[30px] font-bold leading-[1.12] text-ink md:text-[40px]">
          {title}
        </h1>
        {lede ? (
          <p className="mx-auto mt-3.5 max-w-[560px] text-pretty text-center text-[15px] text-slate md:text-[16px]">
            {lede}
          </p>
        ) : null}

        <div className="mt-8">{children}</div>
        {footer ? <div className="mt-8">{footer}</div> : null}
      </main>

      {/*
       * A policy nobody can reach is not published, and Law 25 requires
       * publication. The funnel is where a visitor decides whether to hand over
       * their name and their children's — which is exactly when they should be
       * able to read what happens to it.
       */}
      <footer className="border-t border-border px-5 py-6">
        <nav
          aria-label={t('legal.privacyTitle')}
          className="mx-auto flex max-w-[1100px] flex-wrap gap-4 text-[13px] text-muted"
        >
          <Link to="/legal/privacy" className="underline">
            {t('nav.privacy')}
          </Link>
          <Link to="/legal/terms" className="underline">
            {t('nav.terms')}
          </Link>
        </nav>
      </footer>
    </div>
  )
}

/**
 * The public theme control — the prototype's round icon button.
 *
 * Icon-only, so it carries an `aria-label` rather than relying on a visible
 * one, and `aria-pressed` for the state. The sidebar's toggle is a labelled
 * full-width row and stays as it is; this is the same action at a different
 * size, not a second mechanism — both call `useTheme`.
 */
function ThemeToggle() {
  const { theme, toggle } = useTheme()
  const { t } = useTranslation()
  const Glyph = theme === 'dark' ? IconSun : IconMoon

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={theme === 'dark'}
      aria-label={t('theme.toggle')}
      className="flex h-[40px] w-[40px] items-center justify-center rounded-full border border-border text-ink-soft transition-colors hover:border-ink-soft hover:text-ink"
    >
      <Glyph size={17} title={null} />
    </button>
  )
}

/**
 * The funnel's primary action — `@lb/ui`'s `Button`, full width because on a
 * phone the whole bottom edge is the thumb's target. Kept as a named export
 * so the steps read `<PrimaryButton>` as they always did; the styling
 * decisions (Lila, navy text, 48px pill) now live in one place (D4, WS6).
 */
export function PrimaryButton({
  children,
  disabled,
  busy,
  onClick,
  type = 'button',
}: {
  children: ReactNode
  disabled?: boolean
  busy?: boolean
  onClick?: () => void
  type?: 'button' | 'submit'
}) {
  const { t } = useTranslation()
  return (
    <Button
      type={type}
      onClick={onClick}
      disabled={disabled}
      loading={busy}
      loadingLabel={t('state.busy')}
      fullWidth
    >
      {children}
    </Button>
  )
}

/**
 * The equally-weighted skip.
 *
 * The design is explicit that "Skip for now" is not a de-emphasised link — a
 * parent who does not want to enter their children's names should not have to
 * hunt for the way past. Same height, same hit area, different fill.
 */
export function SecondaryButton({
  children,
  onClick,
}: {
  children: ReactNode
  onClick?: () => void
}) {
  return (
    <Button variant="secondary" onClick={onClick} fullWidth>
      {children}
    </Button>
  )
}

/**
 * `.ob-form-card` — every form step's fields sit on a raised white card, not
 * loose on the canvas.
 *
 * It is not decoration: the funnel is a centered column, and without a card the
 * fields have no left edge to align to and the step reads as a page rather than
 * as a thing to fill in.
 */
export function FormCard({ children }: { children: ReactNode }) {
  return (
    <div className="mx-auto max-w-[620px] rounded-[var(--r-lg)] border border-border bg-surface p-6 text-left sm:p-9">
      {children}
    </div>
  )
}

/**
 * `.ob-back` — the way out, at the top of the column where the eye starts.
 *
 * We had this as a "Retour" button at the BOTTOM, next to Continue, which puts
 * going back and going on in the same cluster — one of them a mis-tap away from
 * losing what was just typed.
 */
export function BackLink({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mb-[22px] inline-flex min-h-[44px] items-center gap-1.5 text-[13.5px] font-semibold text-slate transition-colors hover:text-ink"
    >
      <IconChevronLeft size={16} title={null} />
      {children}
    </button>
  )
}

export function Field({
  label,
  error,
  hint,
  optional,
  children,
}: {
  label: string
  error?: string
  hint?: string
  /** Renders the design's muted "(optional)" beside the label. */
  optional?: string
  children: ReactNode
}) {
  return (
    <label className="mb-5 flex flex-col">
      <span className="mb-[7px] text-[13px] font-semibold text-ink-soft">
        {label}
        {optional ? <span className="ms-1.5 font-medium text-muted">({optional})</span> : null}
      </span>
      {children}
      {/* Errors are inline, under the field, in a warm red — never a toast.
       * The hex pair is the WCAG-AA-checked one from the contrast sweep, not
       * the prototype's `--error`, which fails on the dark canvas. */}
      {error ? (
        <span role="alert" className="mt-1.5 text-[12.5px] font-semibold text-ink">
          {error}
        </span>
      ) : hint ? (
        <span className="mt-1.5 text-[12.5px] text-muted">{hint}</span>
      ) : null}
    </label>
  )
}

/** `.ob-check` — a real checkbox, not a styled div, so it is focusable and announced. */
export function CheckboxRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  children: ReactNode
}) {
  return (
    <label className="mb-3.5 flex cursor-pointer items-start gap-[11px]">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-[3px] h-[17px] w-[17px] flex-shrink-0 cursor-pointer accent-[var(--ink)]"
      />
      <span className="text-[13.5px] text-ink-soft">{children}</span>
    </label>
  )
}

/**
 * `.ob-age-chip` — the funnel's selectable chip.
 *
 * Vert with navy text when on — the accent policy's one selected state (D4),
 * the same as `Pill selected`. It is not a Pill because the funnel's chip is
 * 44px with a 1.5px edge and a role of radio, which the shared pill does not
 * carry; the colours are the same tokens.
 */
export function Chip({
  selected,
  onClick,
  children,
  ...aria
}: {
  selected: boolean
  onClick: () => void
  children: ReactNode
} & Pick<React.ComponentProps<'button'>, 'role' | 'aria-checked'>) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...aria}
      className={cn(
        'min-h-[44px] rounded-full border-[1.5px] px-4 text-[13.5px] font-semibold transition-all',
        selected
          ? 'border-transparent bg-selected text-ink-on-tint'
          : 'border-border bg-surface text-ink-soft hover:border-ink-soft',
      )}
    >
      {children}
    </button>
  )
}

export const inputClass =
  'w-full min-h-[48px] rounded-xl border-[1.5px] border-border bg-surface px-4 py-3 text-[15px] text-ink outline-none transition-[border-color,box-shadow] placeholder:text-muted focus:border-ink focus:shadow-[0_0_0_3px_rgb(41_51_57/0.12)]'
